"use client";

import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Select } from "@package/subframe/components/Select";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherX } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  useCreateColdStorageCommandControllerCreate,
  useUpdateColdStorageCommandControllerUpdate,
} from "@/generated/cold-storage/cold-storage";
import {
  type ColdStorageResponseDto,
  CreateColdStorageDtoType,
  type UpdateColdStorageDtoType,
} from "@/generated/model";
import { useListRoomsQueryControllerList } from "@/generated/rooms/rooms";
import { signedDecimalInputProps } from "@/lib/is-ios";
import { sanitizeDecimalInput } from "@/lib/number-input";

const TYPE_DEFAULTS: Record<
  CreateColdStorageDtoType,
  { min: string; max: string }
> = {
  [CreateColdStorageDtoType.FRIDGE]: { min: "1", max: "5" },
  [CreateColdStorageDtoType.FREEZER]: { min: "-25", max: "-18" },
};

const coldStorageSchema = z
  .object({
    type: z.nativeEnum(CreateColdStorageDtoType),
    name: z
      .string()
      .trim()
      .min(1, "Name is required")
      .max(100, "Name must be less than 100 characters"),
    roomId: z.string().uuid("Room is required"),
    notes: z.string().trim().max(500).optional(),
    targetTempMin: z
      .number({ message: "Min temperature is required" })
      .min(-40, "Min must be at least -40°C")
      .max(40, "Min must be at most 40°C"),
    targetTempMax: z
      .number({ message: "Max temperature is required" })
      .min(-40, "Max must be at least -40°C")
      .max(40, "Max must be at most 40°C"),
  })
  .refine((data) => data.targetTempMin < data.targetTempMax, {
    message: "Min must be less than max",
    path: ["targetTempMax"],
  });

interface ColdStorageFormDialogProps {
  nurseryId: string;
  unit?: ColdStorageResponseDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ColdStorageFormDialog({
  nurseryId,
  unit,
  open,
  onOpenChange,
}: ColdStorageFormDialogProps) {
  const queryClient = useQueryClient();
  const mode = unit ? "edit" : "create";

  const [type, setType] = useState<CreateColdStorageDtoType>(
    CreateColdStorageDtoType.FRIDGE,
  );
  const [name, setName] = useState("");
  const [roomId, setRoomId] = useState("");
  const [notes, setNotes] = useState("");
  const [targetTempMin, setTargetTempMin] = useState(
    TYPE_DEFAULTS[CreateColdStorageDtoType.FRIDGE].min,
  );
  const [targetTempMax, setTargetTempMax] = useState(
    TYPE_DEFAULTS[CreateColdStorageDtoType.FRIDGE].max,
  );
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (unit && mode === "edit") {
      setType(unit.type as unknown as CreateColdStorageDtoType);
      setName(unit.name);
      setRoomId(unit.room.id);
      setNotes(unit.notes ?? "");
      setTargetTempMin(String(unit.targetTempMin));
      setTargetTempMax(String(unit.targetTempMax));
      setIsActive(unit.isActive);
    }
  }, [unit, mode]);

  useEffect(() => {
    if (!open || mode === "create") {
      setType(CreateColdStorageDtoType.FRIDGE);
      setName("");
      setRoomId("");
      setNotes("");
      setTargetTempMin(TYPE_DEFAULTS[CreateColdStorageDtoType.FRIDGE].min);
      setTargetTempMax(TYPE_DEFAULTS[CreateColdStorageDtoType.FRIDGE].max);
      setIsActive(true);
      setErrors({});
    }
  }, [open, mode]);

  useEffect(() => {
    if (mode === "create") {
      setTargetTempMin(TYPE_DEFAULTS[type].min);
      setTargetTempMax(TYPE_DEFAULTS[type].max);
    }
  }, [type, mode]);

  const { data: roomsData, isLoading: isLoadingRooms } =
    useListRoomsQueryControllerList(
      nurseryId,
      { includeInactive: true },
      { query: { enabled: open, refetchOnMount: "always" } },
    );
  const activeRooms = (roomsData?.data ?? []).filter((r) => r.isActive);

  const invalidateColdStorage = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].includes("/cold-storage"),
      refetchType: "all",
    });
  };

  const { mutate: createUnit, isPending: isCreating } =
    useCreateColdStorageCommandControllerCreate({
      mutation: {
        onSuccess: async () => {
          toast.success("Unit created successfully");
          await invalidateColdStorage();
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          const message =
            error instanceof Error ? error.message : "Failed to create unit";
          toast.error(message);
        },
      },
    });

  const { mutate: updateUnit, isPending: isUpdating } =
    useUpdateColdStorageCommandControllerUpdate({
      mutation: {
        onSuccess: async () => {
          toast.success("Unit updated successfully");
          await invalidateColdStorage();
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          const message =
            error instanceof Error ? error.message : "Failed to update unit";
          toast.error(message);
        },
      },
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const parsed = coldStorageSchema.safeParse({
      type,
      name,
      roomId,
      notes: notes.trim() || undefined,
      targetTempMin: Number(targetTempMin),
      targetTempMax: Number(targetTempMax),
    });

    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      parsed.error.issues.forEach((err) => {
        fieldErrors[err.path.join(".")] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    const data = parsed.data;

    if (mode === "create") {
      createUnit({
        nurseryId,
        data: {
          type: data.type,
          name: data.name,
          roomId: data.roomId,
          notes: data.notes,
          targetTempMin: data.targetTempMin,
          targetTempMax: data.targetTempMax,
          isActive,
        },
      });
    } else if (unit) {
      updateUnit({
        nurseryId,
        coldStorageId: unit.id,
        data: {
          type: data.type as unknown as UpdateColdStorageDtoType,
          name: data.name,
          roomId: data.roomId,
          notes: data.notes ?? null,
          targetTempMin: data.targetTempMin,
          targetTempMax: data.targetTempMax,
          isActive,
        },
      });
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-md max-h-[90vh] overflow-y-auto p-0"
      >
        <DialogTitle className="sr-only">
          {mode === "edit" ? "Edit Cold-Storage Unit" : "Add Cold-Storage Unit"}
        </DialogTitle>

        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="flex items-center justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {mode === "edit" ? "Edit Unit" : "Add Fridge or Freezer"}
              </span>
              <span className="text-body font-body text-subtext-color">
                {mode === "edit"
                  ? "Update this cold-storage unit's details."
                  : "Add a new fridge or freezer for this nursery."}
              </span>
            </div>
            <IconButton
              variant="neutral-tertiary"
              size="medium"
              icon={<FeatherX />}
              onClick={() => onOpenChange(false)}
            />
          </div>

          <div className="flex flex-col gap-6 px-6 py-6">
            <Select
              label="Type"
              value={type}
              onValueChange={(v) => setType(v as CreateColdStorageDtoType)}
              disabled={isPending}
            >
              <Select.Item value={CreateColdStorageDtoType.FRIDGE}>
                Fridge
              </Select.Item>
              <Select.Item value={CreateColdStorageDtoType.FREEZER}>
                Freezer
              </Select.Item>
            </Select>

            <TextField
              className="h-auto w-full"
              size="large"
              label="Name"
              error={!!errors.name}
              helpText={errors.name}
            >
              <TextField.Input
                placeholder="e.g., Kitchen Fridge"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isPending}
              />
            </TextField>

            <Select
              label="Room"
              placeholder={
                isLoadingRooms
                  ? "Loading rooms…"
                  : activeRooms.length === 0
                    ? "No active rooms available"
                    : "Select a room"
              }
              value={roomId || undefined}
              onValueChange={(v) => setRoomId(v)}
              disabled={isPending || isLoadingRooms || activeRooms.length === 0}
              error={!!errors.roomId}
              helpText={
                activeRooms.length === 0 && !isLoadingRooms
                  ? "Add at least one active room before configuring a fridge or freezer."
                  : errors.roomId
              }
            >
              {activeRooms.map((room) => (
                <Select.Item key={room.id} value={room.id}>
                  {room.name}
                </Select.Item>
              ))}
            </Select>

            <div className="grid grid-cols-2 gap-4">
              <TextField
                className="h-auto w-full"
                size="large"
                label="Target min (°C)"
                error={!!errors.targetTempMin}
                helpText={errors.targetTempMin}
              >
                <TextField.Input
                  type="text"
                  {...signedDecimalInputProps()}
                  pattern="-?[0-9]*\.?[0-9]*"
                  value={targetTempMin}
                  onChange={(e) =>
                    setTargetTempMin(sanitizeDecimalInput(e.target.value))
                  }
                  disabled={isPending}
                />
              </TextField>
              <TextField
                className="h-auto w-full"
                size="large"
                label="Target max (°C)"
                error={!!errors.targetTempMax}
                helpText={errors.targetTempMax}
              >
                <TextField.Input
                  type="text"
                  {...signedDecimalInputProps()}
                  pattern="-?[0-9]*\.?[0-9]*"
                  value={targetTempMax}
                  onChange={(e) =>
                    setTargetTempMax(sanitizeDecimalInput(e.target.value))
                  }
                  disabled={isPending}
                />
              </TextField>
            </div>

            <TextArea
              className="h-auto w-full"
              label="Notes (optional)"
              error={!!errors.notes}
              helpText={errors.notes}
            >
              <TextArea.Input
                placeholder="e.g., Replaced thermostat 2024-11-12"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isPending}
              />
            </TextArea>

            <Select
              label="Status"
              value={isActive ? "active" : "inactive"}
              onValueChange={(v) => setIsActive(v === "active")}
              disabled={isPending}
            >
              <Select.Item value="active">Active</Select.Item>
              <Select.Item value="inactive">Inactive</Select.Item>
            </Select>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-solid border-secondary-beige-200 px-6 py-4">
            <Button
              type="button"
              variant="neutral-primary"
              size="large"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              size="large"
              disabled={isPending}
            >
              {isPending
                ? mode === "edit"
                  ? "Saving..."
                  : "Creating..."
                : mode === "edit"
                  ? "Save Changes"
                  : "Create Unit"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
